import {tx, ptx, usePageText} from '../lib/page-text';
import { RequestFeedback } from '../components/RequestFeedback';
import { useState } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-store';
import { api } from '../lib/api-client';
import { Modal } from '../components/ui';
import { PlusIcon } from '../components/icons';

interface Channel {
  id: string;
  name: string | null;
  type: 'direct' | 'group' | 'public' | 'stream';
  projectId: string | null;
  ownerId: string | null;
  externalRef: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: { members: number };
}

interface Project { id: string; name: string; slug: string; features: string[] }

interface Analytics {
  channelsTotal: number;
  channelsByType: Record<string, number>;
  messagesLast30d: number;
  membersTotal: number;
  topChannels: { channelId: string; messages: number }[];
}

export function ChatConsolePage() {
  usePageText();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState<{ projectId?: string; type?: string }>({});
  const [searchQ, setSearchQ] = useState('');
  const [activeSearch, setActiveSearch] = useState<string | null>(null);
  const searchResults = useQuery<any[]>({
    queryKey: ['msg-search', activeSearch],
    queryFn: async () => activeSearch
      ? (await api.get(`/me/channels/search/messages?q=${encodeURIComponent(activeSearch)}&limit=50`)).data
      : [],
    enabled: !!activeSearch,
  });

  const projects = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => (await api.get('/me/projects')).data,
  });
  const channels = useQuery<Channel[]>({
    queryKey: ['chat-channels', filter],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (filter.projectId) qs.set('projectId', filter.projectId);
      if (filter.type) qs.set('type', filter.type);
      return (await api.get(`/me/channels${qs.toString() ? '?' + qs : ''}`)).data;
    },
  });
  const analytics = useQuery<Analytics>({
    queryKey: ['chat-analytics'],
    queryFn: async () => (await api.get('/me/channels/analytics/summary')).data,
  });

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{tx("Chat console")}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>{tx("Every channel, message volume, active members — across all projects.")}</p>
        </div>
        <button className="btn lg" onClick={() => setShowCreate(true)}>{ptx("{v0} New channel", {v0: (<PlusIcon size={16}/>)})}</button>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 12, marginBottom: 24 }}>
        <Kpi label={tx("Channels")} value={analytics.data?.channelsTotal ?? '—'} />
        <Kpi label={tx("Members")} value={analytics.data?.membersTotal ?? '—'} />
        <Kpi label={tx("Messages · 30d")} value={analytics.data?.messagesLast30d ?? '—'} />
        <Kpi label={tx("Direct")} value={analytics.data ? (analytics.data?.channelsByType?.direct ?? 0) : '—'} />
        <Kpi label={tx("Group")} value={analytics.data ? (analytics.data?.channelsByType?.group ?? 0) : '—'} />
        <Kpi label={tx("Public")} value={analytics.data ? (analytics.data?.channelsByType?.public ?? 0) : '—'} />
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
        <input
          className="input"
          style={{ flex: '1 1 auto' }}
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setActiveSearch(searchQ.trim() || null); }}
          placeholder={tx("Search messages — supports \"phrase\", word OR word, -negate")}
        />
        <button className="btn" onClick={() => setActiveSearch(searchQ.trim() || null)} disabled={!searchQ.trim()}>{tx("Search")}</button>
        {activeSearch && <button className="btn outline" onClick={() => { setActiveSearch(null); setSearchQ(''); }}>{tx("Clear")}</button>}
      </div>

      {/* Search results */}
      {activeSearch && (
        <div className="card" style={{ marginBottom: 24, padding: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border, #1a1f2a)', fontSize: 12 }} className="muted">
            {searchResults.isLoading ? tx("Searching…") : tx("Matches for {search}: {count}", {count: searchResults.data?.length ?? 0, search: activeSearch})}
          </div>
          {searchResults.data?.map((m: any) => (
            <div key={m.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border, #1a1f2a)', cursor: 'pointer' }}
                 onClick={() => setSelected(m.channelId)}>
              <div className="row-between" style={{ marginBottom: 2 }}>
                <strong style={{ fontSize: 12 }}>{m.authorName}</strong>
                <span className="muted" style={{ fontSize: 11 }}>{new Date(m.createdAt).toLocaleString()}{m.editedAt && tx(" · edited")}</span>
              </div>
              <div style={{ fontSize: 13 }}>{m.body}</div>
              <div className="muted mono" style={{ fontSize: 10, marginTop: 2 }}>{tx("channel: ")}{m.channelId}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{tx("FILTER")}</span>
        <select className="input" style={{ width: 180 }}
          value={filter.projectId ?? ''}
          onChange={(e) => setFilter((f) => ({ ...f, projectId: e.target.value || undefined }))}>
          <option value="">{tx("All projects")}</option>
          {projects.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="input" style={{ width: 150 }}
          value={filter.type ?? ''}
          onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value || undefined }))}>
          <option value="">{tx("All types")}</option>
          <option value="direct">{tx("Direct")}</option>
          <option value="group">{tx("Group")}</option>
          <option value="public">{tx("Public")}</option>
          <option value="stream">{tx("Stream")}</option>
        </select>
      </div>

      <RequestFeedback pending={channels.isPending} failed={channels.isError} hasData={!!channels.data} retry={() => void channels.refetch()} retrying={channels.isFetching} />
      <div className={`dashboard-inspector-grid${selected ? ' has-selection' : ''}`} hidden={!channels.data}>
        <div className="card dashboard-table" style={{ padding: 0 }}>
          {channels.data && channels.data.length === 0
            ? <div style={{ padding: 32, textAlign: 'center' }} className="muted">{tx("No channels yet — click \"New channel\" or use ")}<span className="mono">{tx("POST /v1/me/channels")}</span>{tx(" from your server.")}</div>
            : (
              <table>
                <thead><tr>
                  <th>{tx("Channel")}</th><th>{tx("Type")}</th><th>{tx("Members")}</th><th>{tx("Updated")}</th>
                </tr></thead>
                <tbody>
                  {channels.data?.map((c) => (
                    <tr key={c.id} style={{ cursor: 'pointer', background: selected === c.id ? 'rgba(0,210,255,0.08)' : undefined }}
                        onClick={() => setSelected(c.id)}>
                      <td>
                        <button className="dashboard-row-action" onClick={() => setSelected(c.id)}>{c.name ?? channelLabel(c)}</button>
                        <div className="muted mono" style={{ fontSize: 11 }}>{c.id}</div>
                      </td>
                      <td><span className="badge">{c.type}</span></td>
                      <td className="muted">{c._count?.members ?? 0}</td>
                      <td className="muted">{relativeTime(c.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>

        {selected && (
          <ChannelInspector key={selected}
            channelId={selected}
            onClose={() => setSelected(null)}
            onChange={() => {
              qc.invalidateQueries({ queryKey: ['chat-channels'] });
              qc.invalidateQueries({ queryKey: ['chat-analytics'] });
            }}
          />
        )}
      </div>

      {/* Top channels by activity */}
      {analytics.data && analytics.data.topChannels.length > 0 && (
        <>
          <h2 className="h2" style={{ marginTop: 32, marginBottom: 10 }}>{tx("Most active · 30 days")}</h2>
          <div className="card" style={{ padding: 0 }}>
            <table>
              <thead><tr><th>{tx("Channel ID")}</th><th>{tx("Messages")}</th></tr></thead>
              <tbody>
                {analytics.data.topChannels.map((c) => (
                  <tr key={c.channelId} style={{ cursor: 'pointer' }} onClick={() => setSelected(c.channelId)}>
                    <td className="mono" style={{ fontSize: 12 }}>{c.channelId}</td>
                    <td>{c.messages}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showCreate && projects.data && (
        <CreateChannelModal
          projects={projects.data}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['chat-channels'] }); qc.invalidateQueries({ queryKey: ['chat-analytics'] }); }}
        />
      )}
    </div>
  );
}

function channelLabel(c: Channel): string {
  if (c.type === 'direct') return 'Direct channel';
  if (c.type === 'stream') return `Stream · ${c.externalRef ?? c.id.slice(0, 8)}`;
  return c.type;
}

function relativeTime(iso: string): string {
  const d = Date.now() - new Date(iso).getTime();
  if (d < 60_000) return 'just now';
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  usePageText();
  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
}

// ─── per-channel inspector ──────────────────────────────────────────
interface Message {
  id: string;
  authorId: string | null;
  authorName: string;
  body: string;
  parentId: string | null;
  mentions: string[];
  redacted: boolean;
  editedAt: string | null;
  createdAt: string;
  attachments?: {url:string | null;name?:string;kind?:string}[];
}

function ChannelInspector({ channelId, onClose, onChange }: { channelId: string; onClose: () => void; onChange: () => void }) {
  usePageText();
  const qc=useQueryClient(), user=useAuth(state=>state.user);
  const canEdit=['OWNER','ADMIN','MEMBER'].includes(user?.role ?? '');
  const [draft,setDraft]=useState('');
  const [editing,setEditing]=useState<{id:string;body:string} | null>(null);
  const channel = useQuery<Channel & { members: { userId: string; displayName: string | null; role: string }[] }>({
    queryKey: ['channel', channelId],
    queryFn: async () => (await api.get(`/me/channels/${channelId}`)).data,
  });
  const messages = useInfiniteQuery({
    queryKey:['channel-messages',channelId],initialPageParam:undefined as string | undefined,
    queryFn:async({pageParam})=>(await api.get<Message[]>(`/me/channels/${channelId}/messages?limit=50${pageParam?'&beforeId='+encodeURIComponent(pageParam):''}`)).data,
    getNextPageParam:last=>last.length===50?last[last.length-1].id:undefined,
    refetchInterval:query=>(query.state.data?.pages.length ?? 0)>1?false:5000,
  });
  const refresh=()=>{void qc.invalidateQueries({queryKey:['channel-messages',channelId]});void qc.invalidateQueries({queryKey:['chat-channels']});void qc.invalidateQueries({queryKey:['chat-analytics']});onChange();};
  const send=useMutation({mutationFn:async()=>(await api.post(`/me/channels/${channelId}/messages`,{body:draft.trim(),userId:user!.id,displayName:user?.name ?? user?.email ?? 'Workspace staff'})).data,onSuccess:()=>{setDraft('');refresh();}});
  const edit=useMutation({mutationFn:async()=>(await api.patch(`/me/channels/${channelId}/messages/${editing!.id}`,{body:editing!.body.trim()})).data,onSuccess:()=>{setEditing(null);refresh();}});
  const remove=useMutation({mutationFn:async(id:string)=>(await api.delete(`/me/channels/${channelId}/messages/${id}`)).data,onSuccess:refresh});
  const actions=[send,edit,remove],busy=actions.some(a=>a.isPending),error=actions.find(a=>a.isError)?.error as any;
  const rows=messages.data?.pages.flat() ?? [];

  return (
    <div className="card dashboard-inspector" style={{ padding: 16, position: 'sticky', top: 16, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
      <div className="row-between" style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 600 }}>{channel.data?.name ?? channelId}</div>
        <button className="btn outline sm" onClick={onClose}>{tx("Close")}</button>
      </div>

      <RequestFeedback pending={channel.isPending} failed={channel.isError} retry={()=>void channel.refetch()}/>
      <div style={{ fontSize: 12, marginBottom: 14 }}>
        <div className="muted">{tx("Type: ")}<span style={{ color: 'var(--text)' }}>{channel.data?.type}</span></div>
        <div className="muted">{tx("ID: ")}<span className="mono">{channelId}</span></div>
        {channel.data?.projectId && <div className="muted">{tx("Project: ")}<span className="mono">{channel.data.projectId}</span></div>}
      </div>

      <h4 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-mute)', marginBottom: 6 }}>{ptx("Members ({v0})", {v0: (channel.data?.members?.length ?? 0)})}</h4>
      <div style={{ marginBottom: 14, maxHeight: 140, overflow: 'auto' }}>
        {channel.data?.members?.slice(0, 50).map((m) => (
          <div key={m.userId} style={{ fontSize: 12, padding: '4px 0', display: 'flex', justifyContent: 'space-between' }}>
            <span>{m.displayName ?? m.userId}</span>
            <span className="badge" style={{ fontSize: 10 }}>{m.role}</span>
          </div>
        ))}
      </div>

      <h4 style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-mute)', marginBottom: 6 }}>{ptx("Messages ({v0} loaded)", {v0: (rows.length)})}</h4>
      <RequestFeedback pending={messages.isPending} failed={messages.isError} hasData={!!messages.data} retry={()=>void messages.refetch()}/>
      <div style={{ maxHeight: 400, overflow: 'auto', fontSize: 12 }}>
        {messages.data && rows.length === 0 && <div className="muted">{tx("No messages yet.")}</div>}
        {rows.map((m) => (
          <div key={m.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--border, #1a1f2a)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <strong style={{ fontSize: 11 }}>{m.authorName}</strong>
              <span className="muted" style={{ fontSize: 11 }}>{relativeTime(m.createdAt)}{m.editedAt && tx(" · edited")}</span>
            </div>
            <div style={{ marginTop: 2, opacity: m.redacted ? 0.45 : 1, fontStyle: m.redacted ? 'italic' : 'normal' }}>
              {m.redacted ? tx("(deleted)") : m.body}
            </div>
            {!m.redacted && m.attachments?.map((a,i)=>a.url && /^https?:\/\//i.test(a.url) ? <a key={i} href={a.url} target="_blank" rel="noopener noreferrer">{a.name || tx("Open attachment")}</a> : <span key={i} className="muted">{tx("Attachment unavailable")}</span>)}
            {canEdit && !m.redacted && (editing?.id===m.id ? <div style={{marginTop:8}}><label className="label" htmlFor="edit-channel-message">{tx("Edit message")}</label><textarea id="edit-channel-message" className="input" maxLength={4000} value={editing.body} onChange={e=>setEditing({...editing,body:e.target.value})}/><div style={{display:'flex',gap:8,marginTop:6}}><button className="btn sm" disabled={busy||!editing.body.trim()} onClick={()=>edit.mutate()}>{tx("Save message")}</button><button className="btn outline sm" disabled={busy} onClick={()=>setEditing(null)}>{tx("Cancel edit")}</button></div></div> : <div style={{display:'flex',gap:8,marginTop:6}}><button className="btn outline sm" disabled={busy} onClick={()=>{actions.forEach(a=>a.reset());setEditing({id:m.id,body:m.body});}}>{tx("Edit")}</button><button className="btn outline sm" disabled={busy} onClick={()=>{if(confirm('Redact this message? Its text and attachments will no longer be returned to readers.')){actions.forEach(a=>a.reset());remove.mutate(m.id);}}}>{tx("Redact")}</button></div>)}
            {m.parentId && <div className="muted mono" style={{ fontSize: 10 }}>{tx("↳ thread reply")}</div>}
          </div>
        ))}
      </div>
      <button className="btn outline sm" style={{marginTop:8,marginInlineEnd:8}} disabled={messages.isFetching} onClick={()=>void messages.refetch()}>{tx("Refresh messages")}</button>
      {messages.hasNextPage && <button className="btn outline sm" disabled={messages.isFetchingNextPage} onClick={()=>void messages.fetchNextPage()}>{messages.isFetchingNextPage?tx("Loading…"):tx("Load older messages")}</button>}
      {rows.length>50 && <p className="muted" style={{fontSize:12}}>{tx("History is paused while you browse older messages. Refresh to check for updates.")}</p>}
      {error && <p role="alert" className="error">{error?.response?.data?.message ?? tx("Could not save the message. Try again.")}</p>}
      {canEdit && <form onSubmit={e=>{e.preventDefault();if(draft.trim()){actions.forEach(a=>a.reset());send.mutate();}}} style={{marginTop:14}}><label className="label" htmlFor="channel-message">{ptx("Send as {v0}", {v0: (user?.name ?? tx("workspace staff"))})}</label><textarea id="channel-message" className="input" rows={3} maxLength={4000} value={draft} onChange={e=>setDraft(e.target.value)} placeholder={tx("Write a message")}/><button className="btn" style={{marginTop:8}} disabled={busy||!draft.trim()}>{send.isPending?tx("Sending…"):tx("Send message")}</button></form>}
    </div>
  );
}

// ─── create channel modal ─────────────────────────────────────────
function CreateChannelModal({ projects, onClose, onCreated }: { projects: Project[]; onClose: () => void; onCreated: () => void }) {
  usePageText();
  const [type, setType] = useState<'direct' | 'group' | 'public'>('group');
  const [name, setName] = useState('');
  const [projectId, setProjectId] = useState(projects[0]?.id ?? '');
  const [membersText, setMembersText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const memberIds = membersText.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      const body: any = { type, projectId: projectId || undefined };
      if (type !== 'direct') body.name = name || undefined;
      if (memberIds.length > 0) body.memberIds = memberIds;
      return (await api.post('/me/channels', body)).data;
    },
    onSuccess: onCreated,
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create'),
  });

  return (
    <Modal onClose={onClose}>
      <h2>{tx("New channel")}</h2>
      <p className="modal-subtitle">{tx("Create a channel for your application. Direct channels need two different participant IDs.")}</p>

      <label className="label">{tx("Type")}</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(['direct', 'group', 'public'] as const).map((t) => (
          <button key={t} type="button" className={`btn sm ${type === t ? '' : 'outline'}`} onClick={() => setType(t)}>{t}</button>
        ))}
      </div>

      <label className="label">{tx("Project")}</label>
      <select className="input" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
        <option value="">{tx("No project")}</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {type !== 'direct' && (
        <>
          <div style={{ height: 12 }}/>
          <label className="label">{tx("Name")}</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("e.g. Customer #1234 thread")}/>
        </>
      )}

      <div style={{ height: 12 }}/>
      <label className="label">{ptx("Members {v0}", {v0: (<span className="muted">{tx("(comma-separated user ids; for direct = exactly 2)")}</span>)})}</label>
      <input className="input" value={membersText} onChange={(e) => setMembersText(e.target.value)} placeholder={tx("user_42, support_7")}/>

      {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}
      <div className="row-between" style={{ marginTop: 18 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Create channel")}
        </button>
      </div>
    </Modal>
  );
}
